import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import type { Department } from '../types';
import { X, Upload, FileSpreadsheet, Send, Clock, CheckCircle, XCircle, AlertCircle, Eye, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface BulkDispatchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'create' | 'list' | 'templates';

export const BulkDispatchModal: React.FC<BulkDispatchModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('create');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [dispatches, setDispatches] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDispatches, setIsLoadingDispatches] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importedContacts, setImportedContacts] = useState<any[]>([]);
  const [ignoredContacts, setIgnoredContacts] = useState<any[]>([]);
  const [importSuccess, setImportSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState<any>(null);
  const [showDispatchDetail, setShowDispatchDetail] = useState(false);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsPerPage] = useState(20);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    templateName: 'mensagem_regularizacao',
    departmentId: '',
    scheduledAt: '',
    sendNow: true,
  });

  useEffect(() => {
    if (isOpen) {
      loadDepartments();
      if (activeTab === 'list') {
        loadDispatches();
      }
    }
  }, [isOpen, activeTab]);

  const loadDepartments = async () => {
    try {
      const departments = await api.getDepartments();
      setDepartments(departments);

      // Set Financeiro as default
      const financeiro = departments.find((d: Department) => d.name === 'Financeiro');
      if (financeiro && !formData.departmentId) {
        setFormData(prev => ({ ...prev, departmentId: financeiro.id }));
      }
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadDispatches = async () => {
    setIsLoadingDispatches(true);
    try {
      const response = await api.getBulkDispatches();
      setDispatches(response.dispatches);
    } catch (error) {
      console.error('Error loading dispatches:', error);
    } finally {
      setIsLoadingDispatches(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportedContacts([]);
      setIgnoredContacts([]);
      setImportSuccess(false);
      setError(null);
    }
  };

  const handleImportSpreadsheet = async () => {
    if (!selectedFile) {
      setError('Por favor, selecione uma planilha primeiro');
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportSuccess(false);

    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          // Validate required columns
          if (jsonData.length === 0) {
            setError('A planilha está vazia');
            setIsImporting(false);
            return;
          }

          const firstRow = jsonData[0] as any;
          if (!firstRow.nome || !firstRow.telefone) {
            setError('A planilha deve conter as colunas "nome" e "telefone"');
            setIsImporting(false);
            return;
          }

          // Validate phone numbers
          const validContacts: any[] = [];
          const invalidContacts: any[] = [];

          jsonData.forEach((row: any, index: number) => {
            const rowNum = index + 2; // Excel rows start at 1, first row is header

            // Check for missing data
            if (!row.nome || !row.telefone) {
              invalidContacts.push({
                linha: rowNum,
                nome: row.nome || 'N/A',
                telefone: row.telefone || 'N/A',
                motivo: 'Nome ou telefone ausente'
              });
              return;
            }

            try {
              // Normalize phone using same logic as backend
              // Remove all non-numeric characters (spaces, hyphens, parentheses, etc)
              let cleanPhone = String(row.telefone).replace(/\D/g, '');

              // Remove leading + if present
              if (cleanPhone.startsWith('+')) {
                cleanPhone = cleanPhone.substring(1);
              }

              // Handle different formats
              let normalizedPhone: string | null = null;

              if (cleanPhone.startsWith('55')) {
                // Format: 55xxyyyyyyyyy (already correct format)
                if (cleanPhone.length === 13) {
                  normalizedPhone = cleanPhone;
                }
                // Format: 55xxyyyyyyyy (missing the 9, old format)
                else if (cleanPhone.length === 12) {
                  const areaCode = cleanPhone.substring(2, 4);
                  const number = cleanPhone.substring(4);
                  normalizedPhone = `55${areaCode}9${number}`;
                }
              } else if (cleanPhone.length === 11) {
                // Format: xxyyyyyyyyy (with area code, with 9)
                normalizedPhone = `55${cleanPhone}`;
              } else if (cleanPhone.length === 10) {
                // Format: xxyyyyyyyy (with area code, without 9 - old format)
                const areaCode = cleanPhone.substring(0, 2);
                const number = cleanPhone.substring(2);
                normalizedPhone = `55${areaCode}9${number}`;
              }

              if (!normalizedPhone || normalizedPhone.length !== 13) {
                invalidContacts.push({
                  linha: rowNum,
                  nome: row.nome,
                  telefone: row.telefone,
                  motivo: `Formato de telefone inválido (${cleanPhone.length} dígitos após limpeza)`
                });
                return;
              }

              validContacts.push({
                nome: row.nome,
                telefone: normalizedPhone,
                dias_cancelamento: row.dias_cancelamento || null,
              });
            } catch (err: any) {
              invalidContacts.push({
                linha: rowNum,
                nome: row.nome,
                telefone: row.telefone,
                motivo: err.message || 'Erro ao processar'
              });
            }
          });

          if (validContacts.length === 0) {
            setError('Nenhum contato válido encontrado na planilha');
            setImportedContacts([]);
            setIgnoredContacts(invalidContacts);
            setImportSuccess(false);
            setIsImporting(false);
            return;
          }

          setImportedContacts(validContacts);
          setIgnoredContacts(invalidContacts);
          setImportSuccess(true);
          setError(null);
        } catch (err: any) {
          setError(err.message || 'Erro ao processar planilha');
          setImportedContacts([]);
          setImportSuccess(false);
        } finally {
          setIsImporting(false);
        }
      };

      reader.onerror = () => {
        setError('Erro ao ler o arquivo');
        setIsImporting(false);
      };

      reader.readAsArrayBuffer(selectedFile);
    } catch (err: any) {
      setError('Erro ao importar planilha: ' + err.message);
      setIsImporting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Por favor, selecione uma planilha');
      return;
    }

    if (!formData.name.trim()) {
      setError('Por favor, insira um nome para o disparo');
      return;
    }

    if (!formData.departmentId) {
      setError('Por favor, selecione um departamento');
      return;
    }

    if (!formData.sendNow && !formData.scheduledAt) {
      setError('Por favor, selecione uma data/hora para o disparo');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const data = new FormData();
      data.append('spreadsheet', selectedFile);
      data.append('name', formData.name);
      data.append('templateName', formData.templateName);
      data.append('departmentId', formData.departmentId);

      // If send now, set scheduledAt to current time, otherwise use selected date
      const scheduledAt = formData.sendNow ? new Date().toISOString() : new Date(formData.scheduledAt).toISOString();
      data.append('scheduledAt', scheduledAt);

      const result = await api.createBulkDispatch(data);

      setSuccess(`Disparo "${formData.name}" criado com sucesso! ${result.validationErrors ? `(${result.validationErrors.length} erros na planilha)` : ''}`);
      setShowSuccessModal(true);

      // Reset form
      setSelectedFile(null);
      setImportedContacts([]);
      setIgnoredContacts([]);
      setImportSuccess(false);
      setFormData({
        name: '',
        templateName: 'mensagem_regularizacao',
        departmentId: formData.departmentId, // Keep department selected
        scheduledAt: '',
        sendNow: true,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao criar disparo. Tente novamente.';
      setError(errorMessage);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDispatch = async (dispatch: any) => {
    try {
      const response = await api.getBulkDispatch(dispatch.id);
      setSelectedDispatch(response.dispatch);
      setContactsPage(1); // Reset to first page
      setShowDispatchDetail(true);
    } catch (error) {
      console.error('Error loading dispatch details:', error);
    }
  };

  const handleCancelDispatch = async (id: string) => {
    if (!confirm('Deseja realmente cancelar este disparo?')) return;

    try {
      await api.cancelBulkDispatch(id);
      loadDispatches();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao cancelar disparo');
    }
  };

  const handleDeleteDispatch = async (id: string) => {
    if (!confirm('Deseja realmente excluir este disparo? Esta ação não pode ser desfeita.')) return;

    try {
      await api.deleteBulkDispatch(id);
      loadDispatches();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao excluir disparo');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      PENDING: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Clock },
      IN_PROGRESS: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: Send },
      COMPLETED: { label: 'Concluído', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle },
      FAILED: { label: 'Falhou', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
      CANCELLED: { label: 'Cancelado', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300', icon: XCircle },
    };

    const badge = badges[status as keyof typeof badges] || badges.PENDING;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Disparos em Massa</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('create')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'create'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Iniciar Disparo
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Ver Disparos
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Ver Modelos
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Tab: Iniciar Disparo */}
          {activeTab === 'create' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome do Disparo *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Regularização Abril 2026"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template WhatsApp *
                </label>
                <select
                  value={formData.templateName}
                  onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="mensagem_regularizacao">Mensagem 1 - Oportunidade de Regularização</option>
                  <option value="mensagem_regularizacao_15dias">Mensagem 2 - Última Chance (15 dias)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Departamento de Destino *
                </label>
                <select
                  value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Selecione um departamento</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Clientes que responderem "Sim" serão transferidos para este departamento
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quando Enviar? *
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={formData.sendNow}
                      onChange={() => setFormData({ ...formData, sendNow: true })}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Enviar agora</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!formData.sendNow}
                      onChange={() => setFormData({ ...formData, sendNow: false })}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Agendar para data/hora específica</span>
                  </label>
                  {!formData.sendNow && (
                    <input
                      type="datetime-local"
                      value={formData.scheduledAt}
                      onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ml-6"
                      min={new Date().toISOString().slice(0, 16)}
                      required={!formData.sendNow}
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Planilha de Contatos *
                </label>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-3">
                  <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-2">📋 Formato da Planilha:</p>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
                    <li><strong>nome</strong>: Nome completo do cliente (obrigatório)</li>
                    <li><strong>telefone</strong>: Formatos aceitos: (11) 99999-9999, 11 99999-9999, 34 8800-2003, 11999999999, 5511999999999, +5511999999999 (obrigatório)</li>
                  </ul>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    Formatos aceitos: .xlsx, .xls, .xlsm, .ods, .xltx
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.ods,.xltx"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {!selectedFile ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Clique para selecionar uma planilha
                      </span>
                    </div>
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="w-6 h-6 text-green-600 dark:text-green-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          setImportedContacts([]);
                          setIgnoredContacts([]);
                          setImportSuccess(false);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {!importSuccess && (
                      <button
                        type="button"
                        onClick={handleImportSpreadsheet}
                        disabled={isImporting}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isImporting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Importando...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Importar Planilha
                          </>
                        )}
                      </button>
                    )}

                    {importSuccess && importedContacts.length > 0 && (
                      <div className="space-y-3">
                        {/* Valid Contacts */}
                        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="flex items-start gap-3 mb-3">
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                                ✅ {importedContacts.length} contato(s) válido(s)
                              </p>
                              <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                                Pronto(s) para envio
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 max-h-48 overflow-y-auto border border-green-200 dark:border-green-800 rounded">
                            <table className="min-w-full text-xs">
                              <thead className="bg-green-100 dark:bg-green-900/30 sticky top-0">
                                <tr>
                                  <th className="px-2 py-1 text-left text-green-800 dark:text-green-300">#</th>
                                  <th className="px-2 py-1 text-left text-green-800 dark:text-green-300">Nome</th>
                                  <th className="px-2 py-1 text-left text-green-800 dark:text-green-300">Telefone</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-green-200 dark:divide-green-800">
                                {importedContacts.slice(0, 10).map((contact, index) => (
                                  <tr key={index} className="bg-white dark:bg-gray-800">
                                    <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{index + 1}</td>
                                    <td className="px-2 py-1 text-gray-900 dark:text-white">{contact.nome}</td>
                                    <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{contact.telefone}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {importedContacts.length > 10 && (
                              <p className="p-2 text-center text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/10">
                                ... e mais {importedContacts.length - 10} contato(s)
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Ignored Contacts */}
                        {ignoredContacts.length > 0 && (
                          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                            <div className="flex items-start gap-3 mb-3">
                              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                                  ⚠️ {ignoredContacts.length} contato(s) ignorado(s)
                                </p>
                                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                                  Linhas com erros que não serão enviadas
                                </p>
                              </div>
                            </div>

                            <div className="mt-3 max-h-48 overflow-y-auto border border-yellow-200 dark:border-yellow-800 rounded">
                              <table className="min-w-full text-xs">
                                <thead className="bg-yellow-100 dark:bg-yellow-900/30 sticky top-0">
                                  <tr>
                                    <th className="px-2 py-1 text-left text-yellow-800 dark:text-yellow-300">Linha</th>
                                    <th className="px-2 py-1 text-left text-yellow-800 dark:text-yellow-300">Nome</th>
                                    <th className="px-2 py-1 text-left text-yellow-800 dark:text-yellow-300">Telefone</th>
                                    <th className="px-2 py-1 text-left text-yellow-800 dark:text-yellow-300">Motivo</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-yellow-200 dark:divide-yellow-800">
                                  {ignoredContacts.slice(0, 10).map((contact, index) => (
                                    <tr key={index} className="bg-white dark:bg-gray-800">
                                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{contact.linha}</td>
                                      <td className="px-2 py-1 text-gray-900 dark:text-white">{contact.nome}</td>
                                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{contact.telefone}</td>
                                      <td className="px-2 py-1 text-yellow-700 dark:text-yellow-400 text-xs">{contact.motivo}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {ignoredContacts.length > 10 && (
                                <p className="p-2 text-center text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/10">
                                  ... e mais {ignoredContacts.length - 10} contato(s) ignorado(s)
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !importSuccess}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Criar Disparo
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Tab: Ver Disparos */}
          {activeTab === 'list' && (
            <div className="space-y-4">
              {isLoadingDispatches ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
                </div>
              ) : dispatches.length === 0 ? (
                <div className="text-center py-12">
                  <FileSpreadsheet className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Nenhum disparo criado ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dispatches.map((dispatch) => (
                    <div
                      key={dispatch.id}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-gray-900 dark:text-white">{dispatch.name}</h3>
                            {getStatusBadge(dispatch.status)}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <p className="text-gray-600 dark:text-gray-400">
                              <strong>Template:</strong> {dispatch.templateName === 'mensagem_regularizacao' ? 'Mensagem 1' : 'Mensagem 2'}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              <strong>Departamento:</strong> {dispatch.department.name}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              <strong>Agendado para:</strong> {format(new Date(dispatch.scheduledAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              <strong>Contatos:</strong> {dispatch.totalContacts} ({dispatch.sentCount} enviados, {dispatch.failedCount} falhas)
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleViewDispatch(dispatch)}
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded transition"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {dispatch.status === 'PENDING' && (
                            <button
                              onClick={() => handleCancelDispatch(dispatch.id)}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20 rounded transition"
                              title="Cancelar"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {(dispatch.status === 'COMPLETED' || dispatch.status === 'FAILED' || dispatch.status === 'CANCELLED') && (
                            <button
                              onClick={() => handleDeleteDispatch(dispatch.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded transition"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab: Ver Modelos */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  📱 Template 1: mensagem_regularizacao
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  <strong>Uso:</strong> Primeira mensagem de regularização
                </p>
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                  <p>Olá <strong>{'{{nome}}'}</strong>, tudo bem? Estamos entrando em contato com a oportunidade de regularizar a situação do seu passaporte de acesso ao Itororó Park Club.</p>
                  <p className="mt-2">Você tem interesse em seguir com a negociação?</p>
                  <div className="mt-3 flex gap-2">
                    <button className="px-3 py-1 bg-green-600 text-white rounded text-xs">Sim</button>
                    <button className="px-3 py-1 bg-red-600 text-white rounded text-xs">Não</button>
                  </div>
                </div>
              </div>

              <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  📱 Template 2: mensagem_regularizacao_15dias
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  <strong>Uso:</strong> Mensagem de última chance (15 dias após primeira mensagem)
                </p>
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                  <p>Olá <strong>{'{{nome}}'}</strong>, tudo bem?</p>
                  <p className="mt-2">Essa é a última chance de manter seu passaporte do Itororó Park Club, em um prazo de <strong>{'{{dias_cancelamento}}'}</strong> dias você perderá o acesso ao clube de forma definitiva e também a titularidade de sócio.</p>
                  <p className="mt-2">Quer conversar sobre como regularizar sua situação?</p>
                  <div className="mt-3 flex gap-2">
                    <button className="px-3 py-1 bg-green-600 text-white rounded text-xs">Sim</button>
                    <button className="px-3 py-1 bg-red-600 text-white rounded text-xs">Não</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Dispatch Detail Modal */}
        {showDispatchDetail && selectedDispatch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Detalhes do Disparo: {selectedDispatch.name}
                </h2>
                <button
                  onClick={() => {
                    setShowDispatchDetail(false);
                    setSelectedDispatch(null);
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                      <div className="mt-1">{getStatusBadge(selectedDispatch.status)}</div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Template</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedDispatch.templateName === 'mensagem_regularizacao' ? 'Mensagem 1' : 'Mensagem 2'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total de Contatos</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedDispatch.totalContacts}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Enviados</p>
                      <p className="text-sm font-medium text-green-600 dark:text-green-400">{selectedDispatch.sentCount}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Falhas</p>
                      <p className="text-sm font-medium text-red-600 dark:text-red-400">{selectedDispatch.failedCount}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Agendado para</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {format(new Date(selectedDispatch.scheduledAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </p>
                    </div>
                  </div>

                  {selectedDispatch.contacts && selectedDispatch.contacts.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Contatos ({selectedDispatch.contacts.length})</h3>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Nome</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Telefone</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Resposta</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {selectedDispatch.contacts
                                .slice((contactsPage - 1) * contactsPerPage, contactsPage * contactsPerPage)
                                .map((contact: any) => (
                                  <tr key={contact.id}>
                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{contact.name}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{contact.phone}</td>
                                    <td className="px-4 py-2 text-sm">
                                      <span className={`px-2 py-1 rounded-full text-xs ${
                                        contact.status === 'SENT' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                        contact.status === 'FAILED' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                        contact.status === 'RESPONDED_YES' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                        contact.status === 'RESPONDED_NO' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                                      }`}>
                                        {contact.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                                      {contact.responseType || '-'}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {selectedDispatch.contacts.length > contactsPerPage && (
                          <div className="bg-gray-50 dark:bg-gray-900/50 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Mostrando {((contactsPage - 1) * contactsPerPage) + 1} a {Math.min(contactsPage * contactsPerPage, selectedDispatch.contacts.length)} de {selectedDispatch.contacts.length} contatos
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setContactsPage(p => Math.max(1, p - 1))}
                                disabled={contactsPage === 1}
                                className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                Anterior
                              </button>
                              <span className="text-xs text-gray-700 dark:text-gray-300">
                                Página {contactsPage} de {Math.ceil(selectedDispatch.contacts.length / contactsPerPage)}
                              </span>
                              <button
                                onClick={() => setContactsPage(p => Math.min(Math.ceil(selectedDispatch.contacts.length / contactsPerPage), p + 1))}
                                disabled={contactsPage === Math.ceil(selectedDispatch.contacts.length / contactsPerPage)}
                                className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                Próxima
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
                Disparo Criado com Sucesso!
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
                {success}
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccess(null);
                    setActiveTab('list');
                    loadDispatches();
                  }}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                  Ver Disparos
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccess(null);
                  }}
                  className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
                Erro ao Criar Disparo
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
                {error}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setShowErrorModal(false);
                    setError(null);
                  }}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
